"use client";

import { ArrowLeft, Calendar, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Separator } from "@/components/ui/separator";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

function formatDate(dateString?: string): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function getInitials(name?: string): string {
  if (!name) return "E";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="h-6 w-24 bg-muted animate-pulse mb-8 rounded-md" />
        <div className="h-12 sm:h-16 w-3/4 bg-muted animate-pulse mb-6 rounded-md" />
        <div className="flex gap-4 mb-10">
          <div className="h-10 w-10 bg-muted animate-pulse rounded-lg" />
          <div className="space-y-2">
            <div className="h-4 w-32 bg-muted animate-pulse rounded-md" />
            <div className="h-3 w-24 bg-muted animate-pulse rounded-md" />
          </div>
        </div>
        <div className="aspect-[16/9] w-full bg-muted animate-pulse mb-10 rounded-lg" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted animate-pulse rounded-md" />
          <div className="h-4 w-full bg-muted animate-pulse rounded-md" />
          <div className="h-4 w-5/6 bg-muted animate-pulse rounded-md" />
          <div className="h-4 w-full bg-muted animate-pulse rounded-md" />
          <div className="h-4 w-4/6 bg-muted animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <p className="text-muted-foreground">Unable to load this article. Please try again later.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <p className="text-muted-foreground">No article found.</p>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "ESO Editorial";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"];
  const authorLink = post.author?.link;
  const categories = post.categories || [];
  const tags = post.tags || [];

  return (
    <article className="w-full">
      {}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-8 lg:pb-12">
        <Link
          to="/CaseStudiesPage"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to case studies</span>
        </Link>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat, i) => (
              <span
                data-index={i}
                key={cat.id}
                className="text-xs uppercase tracking-widest text-primary font-default font-semibold"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}

        <h1 className="font-serif font-default text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-foreground leading-tight mb-8">
          {post.title}
        </h1>

        {post.excerpt && (
          <div
            className="text-lg sm:text-xl text-muted-foreground font-default leading-relaxed mb-10"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        )}

        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 rounded-lg">
              {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
              <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-medium rounded-lg">
                {getInitials(authorName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-foreground font-medium">{authorName}</span>
              <span className="text-xs">ESO Contributor</span>
            </div>
          </div>
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <time dateTime={post.date}>{formatDate(post.date)}</time>
            </div>
          )}
        </div>
      </div>

      {}
      {post.featuredImage && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 lg:mb-16">
          <div className="overflow-hidden rounded-lg bg-muted">
            <img
              src={post.featuredImage}
              alt={post.title}
              loading="lazy"
              decoding="async"
              className="w-full h-auto object-cover aspect-[16/9]"
            />
          </div>
        </div>
      )}

      {}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 lg:pb-16">
        {post.content ? (
          <div
            className="prose prose-lg max-w-none font-default text-foreground leading-relaxed [&>p]:mb-6 [&>p]:text-base [&>p]:sm:text-lg [&>h2]:font-serif [&>h2]:text-3xl [&>h2]:font-medium [&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:text-foreground [&>h3]:font-serif [&>h3]:text-2xl [&>h3]:font-medium [&>h3]:mt-10 [&>h3]:mb-3 [&>h3]:text-foreground [&>ul]:mb-6 [&>ul]:pl-6 [&>ul]:list-disc [&>ol]:mb-6 [&>ol]:pl-6 [&>ol]:list-decimal [&>li]:mb-2 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:my-8 [&>blockquote]:text-muted-foreground [&>a]:text-primary [&>a]:underline [&_img]:rounded-lg [&_img]:my-8"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        ) : (
          <p className="text-muted-foreground">No content available.</p>
        )}

        {tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-border flex flex-wrap gap-2">
            <span className="text-sm text-muted-foreground mr-2 self-center">Tags:</span>
            {tags.map((tag, i) => (
              <Link
                data-index={i}
                key={tag.id}
                to={tag.link || "#"}
                className="text-xs uppercase tracking-wider px-3 py-1.5 bg-secondary text-secondary-foreground hover:bg-accent transition-colors rounded-md"
              >
                {tag.name}
              </Link>
            ))}
          </div>
        )}
      </div>

      {}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-20">
        <Separator className="mb-12" />
        <Card className="bg-secondary/50 border-border rounded-lg shadow-sm">
          <CardContent className="p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <Avatar className="h-20 w-20 rounded-lg shrink-0">
                {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
                <AvatarFallback className="bg-primary text-primary-foreground text-xl font-medium rounded-lg">
                  {getInitials(authorName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  Written by
                </p>
                <h3 className="font-serif font-default text-2xl font-medium text-foreground mb-3">
                  {authorName}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {"ESO contributor focused on documenting the impact of Community Capability Development projects and the pathways they create for emerging talent."}
                </p>
                {authorLink && (
                  <Link to={authorLink}>
                    <Button variant="outline" size="sm" className="rounded-md">
                      <User className="h-4 w-4" />
                      <span>View profile</span>
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </article>
  );
}

function RelatedPostsConsumer({ currentPostId }: { currentPostId: string }) {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[0, 1, 2].map((i) => (
          <div data-index={i} key={i} className="space-y-4">
            <div className="aspect-[4/3] bg-muted animate-pulse rounded-lg" />
            <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
            <div className="h-6 w-full bg-muted animate-pulse rounded-md" />
            <div className="h-4 w-3/4 bg-muted animate-pulse rounded-md" />
          </div>
        ))}
      </div>
    );
  }

  const related = (posts || []).filter((p) => p.id !== currentPostId).slice(0, 3);

  if (related.length === 0) {
    return (
      <p className="text-muted-foreground text-center">No related stories available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
      {related.map((post, i) => (
        <Link
          data-index={i}
          key={post.id}
          to={post.link || "#"}
          className="group flex flex-col"
        >
          <div className="aspect-[4/3] overflow-hidden bg-muted mb-5 rounded-lg">
            {post.featuredImage ? (
              <img
                src={post.featuredImage}
                alt={post.title}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-muted to-accent" />
            )}
          </div>
          {post.categories && post.categories.length > 0 && (
            <span className="text-xs uppercase tracking-widest text-primary font-default font-semibold mb-2">
              {post.categories[0].name}
            </span>
          )}
          <h3 className="font-serif font-default text-xl lg:text-2xl font-medium text-foreground leading-snug mb-3 group-hover:text-primary transition-colors">
            {post.title}
          </h3>
          {post.excerpt && (
            <div
              className="text-sm text-muted-foreground line-clamp-2"
              dangerouslySetInnerHTML={{ __html: post.excerpt }}
            />
          )}
          {post.date && (
            <time
              dateTime={post.date}
              className="text-xs text-muted-foreground mt-3 uppercase tracking-wider"
            >
              {formatDate(post.date)}
            </time>
          )}
        </Link>
      ))}
    </div>
  );
}

function SinglePostInner() {
  const { posts } = usePosts();
  const currentPost = posts && posts[0];
  const postType = currentPost?.customFields?.post_type || "post";

  const relatedQueryParams: Record<string, any> = {
    post_type: postType,
    per_page: 4,
    orderby: "date",
    order: "desc",
  };

  if (currentPost?.categories && currentPost.categories.length > 0) {
    relatedQueryParams.categories = currentPost.categories.map((c) => c.id);
  }

  const relatedWpQuery = new WP_Query(relatedQueryParams);

  return (
    <>
      <SinglePostConsumer />

      {currentPost && (
        <div className="border-t border-border bg-secondary/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
              <div>
                <p className="text-xs uppercase tracking-widest text-primary font-default font-semibold mb-3">
                  {"Continue reading"}
                </p>
                <h2 className="font-serif font-default text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground">
                  {"Related stories"}
                </h2>
              </div>
              <Link to="/CaseStudiesPage">
                <Button variant="outline" className="rounded-md">
                  <span>Browse all</span>
                  <ArrowLeft className="h-4 w-4 rotate-180" />
                </Button>
              </Link>
            </div>

            <PostsProvider wp_query={relatedWpQuery}>
              <RelatedPostsConsumer currentPostId={currentPost.id} />
            </PostsProvider>
          </div>
        </div>
      )}
    </>
  );
}

export default function Article() {
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="6919" id="single-post-template-section" className="bg-background text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostInner />
      </PostsProvider>
    </section>
  );
}